import React from "react";
import { Button, ButtonProps } from "@/components/ui/button";
import { Sparkle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link, LinkProps } from "react-router-dom";

interface UpgradeToPlusButtonProps extends Omit<LinkProps, "children" | "to"> {
  variant?: "default" | "secondary" | "outline" | "ghost" | "link" | "destructive" | "circular" | "purple";
  size?: "default" | "sm" | "lg" | "icon";
  showIcon?: boolean;
  customText?: string;
  onClick?: () => void;
}

/**
 * Standardized "Upgrade to Plus" button component
 * 
 * Ensures consistent styling and behavior across the application
 * Always opens the WaitlistDialog when clicked
 */
export function UpgradeToPlusButton({
  variant = "purple",
  size = "default",
  showIcon = true,
  customText,
  className,
  onClick,
  ...props
}: UpgradeToPlusButtonProps) {
  const buttonText = customText || "Waitlist for Plus";
  
  // Determine the appropriate styling based on size
  const sizeStyles = {
    default: "py-2 px-4 text-sm",
    sm: "py-1 px-3 text-xs",
    lg: "py-3 px-6 text-base",
    icon: "p-2"
  }[size];
  
  // Create combined click handler if onClick is provided
  const handleClick = onClick ? (e: React.MouseEvent) => {
    // Find the email input section after navigation completes
    setTimeout(() => {
      const emailSection = document.querySelector('.card-footer-email-section');
      if (emailSection) {
        emailSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
    
    onClick();
  } : undefined;
  
  return (
    <Link
      to="/pricing"
      onClick={handleClick}
      className={cn(
        "inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
        sizeStyles,
        variant === "purple" && "bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-full shadow-md hover:shadow-lg hover:from-purple-700 hover:to-violet-700 hover:scale-105 active:scale-95",
        variant === "outline" && "border border-purple-200 text-purple-600 rounded-full bg-white/90 backdrop-blur-sm hover:bg-purple-50 hover:border-purple-300 hover:shadow-md hover:scale-105 active:scale-95",
        variant === "ghost" && "hover:bg-accent hover:text-accent-foreground rounded-full",
        variant === "link" && "text-primary underline-offset-4 hover:underline",
        variant === "circular" && "rounded-full shadow-md hover:shadow-lg hover:scale-105 active:scale-95 bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100",
        className
      )}
      {...props}
    >
      {buttonText}
      {showIcon && 
        <span className="relative">
          <Sparkle className="h-4 w-4 pointer-events-none animate-pulse text-amber-400" />
          <span className="absolute inset-0 animate-ping opacity-75">
            <Sparkle className="h-4 w-4 text-amber-300" />
          </span>
        </span>
      }
    </Link>
  );
}
